import type { AnyAction, Dispatch } from "redux"
import { bidSelector } from "@kiwicom/frontend-common/services/booking/booking"
import { languageSelector } from "@kiwicom/frontend-common/services/options/selectors"
import apiBookingTokens from "@kiwicom/frontend-common/services/apiBookingTokens/apiBookingTokens"
import session from "@kiwicom/frontend-common/browser/session"
import { MockParams } from "@kiwicom/darwin/mock"

import { baseApiURLSelector, mmb2BasePathSelector } from "./selectors"
import mmbTrack from "../tracking/mmbTrack"
import {
  ErrorOccurredAction,
  MMB2Module,
  RedirectAction,
} from "../tracking/constants/trackingEvents"
import type { DynamicRouteValues, MMB1MigratedModals, MMB2FeatureRoute } from "./types"
import { MMB1_MIGRATED_MODALS } from "./types"
import createMMB2RedirectLink, { MMB2URLProps } from "./createMMB2RedirectLink"
import { GetState } from "@kiwicom/frontend-common/types/state"

/**
 * Generates route path to MMB2 feature.
 *
 * @param mmb1ModalName - mmb1 modal name
 * @param dynamicRouteValues - object with dynamic route values, such as requestId, etc.
 * @returns route path to MMB2 feature
 */
const getMMB2FeatureRoute = (
  mmb1ModalName: MMB1MigratedModals,
  dynamicRouteValues: DynamicRouteValues,
): MMB2FeatureRoute => {
  switch (mmb1ModalName) {
    case MMB1_MIGRATED_MODALS.INSURANCE:
      return "insurance"
    case MMB1_MIGRATED_MODALS.SPECIAL_ASSISTANCE:
      return "special-assistance"
    case MMB1_MIGRATED_MODALS.INVOICES:
      return "invoices"
    case MMB1_MIGRATED_MODALS.CHECK_IN_HEALTH_DECLARATION:
      return "check-in/health-declaration"
    case MMB1_MIGRATED_MODALS.CHECK_IN_DOCUMENTS:
      return "check-in/documents"
    case MMB1_MIGRATED_MODALS.DISRUPTION_PROTECTION_UPSELL:
      return "disruption-protection"
    case MMB1_MIGRATED_MODALS.SCHEDULE_CHANGE:
      return "schedule-change"
    case MMB1_MIGRATED_MODALS.BAGGAGE:
      return "baggage"
    case MMB1_MIGRATED_MODALS.PAYMENT:
      return `payment/${dynamicRouteValues.basketId}`
    case MMB1_MIGRATED_MODALS.REQUESTS_REFUNDS:
      if (dynamicRouteValues.offerId) {
        return `refunds/offers/${dynamicRouteValues.offerId}/refund-method`
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: `MMB2 feature route is missing offerId value`,
        },
        { mmb1ModalName },
      )
      throw new Error(
        `MMB2 feature route is missing offerId value for mmb1 modal name: ${mmb1ModalName}`,
      )
    case MMB1_MIGRATED_MODALS.OOP_KIWI_OFFER:
      if (dynamicRouteValues.offerId) {
        return `refunds/offers/${dynamicRouteValues.offerId}/kiwi-offer`
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: `MMB2 feature route is missing offerId value`,
        },
        { mmb1ModalName },
      )
      throw new Error("MMB2 feature route is missing offerId value")
    case MMB1_MIGRATED_MODALS.ALTERNATIVE_METHOD_OFFER:
      if (dynamicRouteValues.offerId) {
        return `refunds/offers/${dynamicRouteValues.offerId}/alternative-method`
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: `MMB2 feature route is missing offerId value`,
        },
        { mmb1ModalName },
      )
      throw new Error("MMB2 feature route is missing offerId value")
    case MMB1_MIGRATED_MODALS.PROACTIVE_REFUND_OFFER:
      if (dynamicRouteValues.offerId) {
        return `refunds/offers/${dynamicRouteValues.offerId}/proactive-refund`
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: `MMB2 feature route is missing offerId value`,
        },
        { mmb1ModalName },
      )
      throw new Error("MMB2 feature route is missing offerId value")
    case MMB1_MIGRATED_MODALS.NON_PROCESSED_BOOKING:
      return "refunds/offers/nonprocessed-booking/intro"
    case MMB1_MIGRATED_MODALS.REFUND_DETAILS:
      if (dynamicRouteValues.requestId) {
        return `refunds/requests/${dynamicRouteValues.requestId}/details`
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: "MMB2 feature route is missing requestId value",
        },
        { mmb1ModalName },
      )
      throw new Error("MMB2 feature route is missing offerId value")
    case MMB1_MIGRATED_MODALS.SEATING:
      return "seating"
    case MMB1_MIGRATED_MODALS.PASSENGER_DETAILS:
      if (dynamicRouteValues.passengerId) {
        return "passenger-details"
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: "MMB2 feature route is missing passengerId value",
        },
        { mmb1ModalName },
      )
      throw new Error("MMB2 feature route is missing passengerId value")
    case MMB1_MIGRATED_MODALS.DOCUMENT_VERIFICATION:
      return "document-verification/upload"
    case MMB1_MIGRATED_MODALS.ITINERARY_DETAILS:
      return "itinerary-details"
    case MMB1_MIGRATED_MODALS.REFUND_OPTIONS:
      return "refunds/options"
    case MMB1_MIGRATED_MODALS.INSTANT_CREDIT:
      return "refunds/options/instant-credit"
    case MMB1_MIGRATED_MODALS.ASSISTED_REFUND:
      return "refunds/options/assisted-refund"
    case MMB1_MIGRATED_MODALS.INVOLUNTARY_ASSISTED_REFUND:
      return "refunds/options/involuntary-assisted-refund"
    case MMB1_MIGRATED_MODALS.FARE_TYPE_REFUND:
      return "refunds/options/fare-type"
    case MMB1_MIGRATED_MODALS.AUTOMATIC_VOID:
      return "refunds/options/automatic-void"
    case MMB1_MIGRATED_MODALS.MEDICAL:
      return "refunds/options/medical"
    case MMB1_MIGRATED_MODALS.DEATH:
      return "refunds/options/death"
    case MMB1_MIGRATED_MODALS.DISRUPTION_REFUND:
      return "refunds/options/disruption"
    case MMB1_MIGRATED_MODALS.UNCONFIRMED_BOOKING_CANCELLATION:
      return "refunds/options/unconfirmed-booking-cancellation"
    case MMB1_MIGRATED_MODALS.EXPENSE_REFUND:
      if (dynamicRouteValues.expenseType) {
        return `refunds/options/expense/${dynamicRouteValues.expenseType}`
      }
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: `MMB2 feature route is missing expenseType value`,
        },
        { mmb1ModalName },
      )
      throw new Error("MMB2 feature route is missing expenseType value")
    case MMB1_MIGRATED_MODALS.UNCONFIRMED_BOOKING_CANCELLATION_REQUEST:
      return "refunds/requests/unconfirmed-booking-cancellation"
    case MMB1_MIGRATED_MODALS.ITINERARY_CHANGE:
      return "itinerary-change"
    default:
      mmbTrack(
        {
          module: MMB2Module,
          action: ErrorOccurredAction,
          detail: "MMB2 feature route not found for mmb1 modal name",
        },
        {
          mmb1ModalName,
        },
      )
      // Fix this the next time the file is edited. (Flow to TS)
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`MMB2 feature route not found for mmb1 modal name: ${mmb1ModalName}`)
  }
}

export type TrackedValue = string | number

type ShowFeatureInMMB2 = {
  nextModal: MMB1MigratedModals
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state: any
  dynamicRouteValues: DynamicRouteValues
  // Fix this the next time the file is edited. (Flow to TS)
  // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
  additionalTrackingProps?: {
    [key: string]: TrackedValue
  }
}

/**
 * does not mutate the original url, instead it return new one
 * @param link
 * @param queryParam
 * @returns {string}
 */
const removeQueryParamFromURL = (link: string, queryParam: string): string => {
  const url = new URL(link)
  url.searchParams.delete(queryParam)
  return url.toString()
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDarwinFeaturesToPropagateToMMB2 = (query: any) => {
  // @ts-expect-error: Flow to TS migration.
  const darwinFeatures = window.__DARWIN__.features

  // If there aren't any darwin features in URL don't pass the darwin features to MMB2
  const queryParams = Object.keys(query)
  if (
    !queryParams.includes("darwin_mock_feature_on") &&
    !queryParams.includes("darwin_mock_feature_off")
  ) {
    return {}
  }

  return darwinFeatures
}

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDarwinTestsToPropagateToMMB2 = (query: any) =>
  // @ts-expect-error: Flow to TS migration.
  Object.keys(query).includes(MockParams.MOCK_TEST) ? window.__DARWIN__.tests : []

// Fix this the next time the file is edited. (Flow to TS)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDarwinWinnersToPropagateToMMB2 = (query: any) =>
  // @ts-expect-error: Flow to TS migration.
  Object.keys(query).includes(MockParams.MOCK_WINNER) ? window.__DARWIN__.winners : []

// Redux state is not typed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getQueryDataSelector = (state: any): MMB2URLProps["query"] => {
  const bid = bidSelector(state)
  const { platform, iframe } = state.query
  const { UTMs } = session

  return {
    baseApiUrl: baseApiURLSelector(state),
    simpleToken: bid ? apiBookingTokens.getSimple(bid) : null,
    platform,
    iframe,
    UTMs,
    darwinFeatures: getDarwinFeaturesToPropagateToMMB2(state.query),
    darwinTests: getDarwinTestsToPropagateToMMB2(state.query),
    darwinWinners: getDarwinWinnersToPropagateToMMB2(state.query),
  }
}

export const showFeatureInMMB2 = ({
  nextModal,
  state,
  dynamicRouteValues,
  additionalTrackingProps,
}: ShowFeatureInMMB2): void => {
  const bid = bidSelector(state)
  const mmb2BasePath = mmb2BasePathSelector(state)
  const mmb2FeatureRoute = getMMB2FeatureRoute(nextModal, dynamicRouteValues)
  const language = languageSelector(state)
  const queryData = getQueryDataSelector(state)
  const { baseApiUrl, platform, iframe } = queryData

  void createMMB2RedirectLink({
    bid,
    mmb2BasePath,
    mmb2FeatureRoute,
    language,
    query: queryData,
    dynamicRouteValues,
  }).then((redirectLink) => {
    mmbTrack(
      {
        module: MMB2Module,
        action: RedirectAction,
        detail: "redirect to mmb2",
      },
      {
        ...additionalTrackingProps,
        nextModal,
        mmb2BasePath,
        mmb2FeatureRoute,
        baseApiUrl,
        mmbRedirectLink: removeQueryParamFromURL(redirectLink, "simple_token"),
        platform,
        iframe,
        type: "redirect to mmb2",
      },
    )

    // If deeplink is present, replace instead of pushing new entry to avoid loop when going back in history from MMB2 to MMB1
    if (state.query.deeplink) {
      window.location.replace(redirectLink)
    } else {
      window.location.assign(redirectLink)
    }
  })
}

/**
 * Action to redirect to mmb2 feature.
 * Should be updated to accept new features as they are ready on mmb2
 *  - Add modal name to MMB1_MIGRATED_MODALS and getMMB2FeatureRoute()
 *
 * @param nextModal - mmb1 modal name
 * @param dynamicRouteValues - object with dynamic route values, such as requestId, etc.
 * @param additionalTrackingProps - object with additional tracking props to distinguish redirects for analytics purpose (e.g. pending refund reason, etc.)
 * @returns void, redirects to mmb2
 */
const mmb2RedirectAction = (
  nextModal: MMB1MigratedModals,
  dynamicRouteValues: DynamicRouteValues = {},
  additionalTrackingProps: Record<string, TrackedValue> = {},
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
): AnyAction => (_dispatch: Dispatch, getState: GetState) => {
  const state = getState()
  showFeatureInMMB2({ nextModal, state, dynamicRouteValues, additionalTrackingProps })
}
export default mmb2RedirectAction
